import cx from 'classnames';
import { useCallback, useState, type FunctionComponent } from 'react';

import LikeIcon from '@atlaskit/icon/core/thumbs-up';
import { Analytics } from '@trello/atlassian-analytics';
import { useDynamicConfig } from '@trello/dynamic-config';
import {
  AiLabsEntryPointId,
  useFeedbackCollector as useJiraFeedbackCollector,
} from '@trello/feedback-collector';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CloseIcon } from '@trello/nachos/icons/close';

import type { FeatureNames } from './featuresInfo';
import { NegativeFeedbackDetail } from './NegativeFeedbackDetail';
import { useFeedbackCollector } from './useFeedbackCollector';

import * as styles from './FeedbackCollector.module.less';

interface FeedbackCollectorProps {
  featureName: FeatureNames;
  flagTreatment?: boolean;
}

export const FeedbackCollector: FunctionComponent<FeedbackCollectorProps> = ({
  featureName,
  flagTreatment,
}) => {
  const [shouldShowFeedbackDetail, setShouldShowFeedbackDetail] =
    useState(false);
  const [selectedThumbButton, setSelectedThumbButton] = useState<
    'down' | 'up' | null
  >(null);
  const [wasFeedbackSubmitted, setWasFeedbackSubmitted] =
    useState<boolean>(false);

  const isInternalAtlassian = useDynamicConfig('trello_web_atlassian_team');
  const {
    getFeedbackCollectorProjectKey,
    isOpen,
    dismissFeedbackCollector,
    traceId,
  } = useFeedbackCollector({
    featureName,
  });

  const getFeedbackContextOverride = useCallback(() => {
    return getFeedbackCollectorProjectKey();
  }, [getFeedbackCollectorProjectKey]);

  const {
    FeedbackCollector: JiraFeedbackCollector,
    showFeedbackCollector: showJiraFeedbackCollector,
  } = useJiraFeedbackCollector({
    entrypointId: AiLabsEntryPointId,
    source: 'aiLabsFeedbackCollector',
    feedbackCollectorProps: {
      feedbackTitle: 'Share your thoughts on how Rovo did',
    },
    getFeedbackContextOverride,
  });

  const handleThumbsUp = useCallback(() => {
    setShouldShowFeedbackDetail(false);
    setSelectedThumbButton(selectedThumbButton === 'up' ? null : 'up');

    Analytics.sendClickedButtonEvent({
      buttonName: 'aiLabsFeedbackCollectorThumbsUpButton',
      source: 'aiLabsFeedbackCollector',
      attributes: {
        aiLabsFeatureName: featureName || '',
        aiLabsSentimentModifier: 1, // Assuming positive sentiment for thumbs up
        taskId: traceId,
      },
    });

    if (flagTreatment) {
      dismissFeedbackCollector();

      showFlag({
        id: 'aiLabsFeedbackCollectorSuccessMessage',
        title: 'Thank you for your feedback!',
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 2000,
      });
    }

    setWasFeedbackSubmitted(true);
  }, [
    selectedThumbButton,
    featureName,
    traceId,
    flagTreatment,
    dismissFeedbackCollector,
  ]);

  const handleThumbsDown = useCallback(() => {
    setShouldShowFeedbackDetail(true);
    setSelectedThumbButton(selectedThumbButton === 'down' ? null : 'down');

    Analytics.sendClickedButtonEvent({
      buttonName: 'aiLabsFeedbackCollectorThumbsDownButton',
      source: 'aiLabsFeedbackCollector',
      attributes: {
        aiLabsFeatureName: featureName || '',
        aiLabsSentimentModifier: -1, // Assuming negative sentiment for thumbs down
        taskId: traceId,
      },
    });
  }, [featureName, selectedThumbButton, traceId]);

  const handleFeedbackSubmitted = useCallback(() => {
    if (flagTreatment) {
      dismissFeedbackCollector();

      showFlag({
        id: 'aiLabsFeedbackCollectorSuccessMessage',
        title: 'Thank you for your feedback!',
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 2000,
      });
    }

    setWasFeedbackSubmitted(true);
  }, [flagTreatment, dismissFeedbackCollector]);

  if (!isInternalAtlassian || (flagTreatment && !isOpen)) {
    return null;
  }

  const feedbackCollectorContents = (
    <>
      <JiraFeedbackCollector />
      <div className={styles.feedbackButtonsContainer}>
        <div className={styles.titleAndCloseButton}>
          <span>How did Rovo do?</span>

          {flagTreatment && (
            <Button
              className={styles.closeButton}
              onClick={dismissFeedbackCollector}
            >
              <CloseIcon label="Close feedback collector" />
            </Button>
          )}
        </div>
        <div>
          <Button
            onClick={handleThumbsUp}
            className={cx(styles.positiveFeedbackButton, {
              [styles.selected]: selectedThumbButton === 'up',
            })}
          >
            <LikeIcon label={'Valuable'} spacing="spacious"></LikeIcon>
          </Button>
          <Button
            onClick={handleThumbsDown}
            className={cx(styles.negativeFeedbackButton, {
              [styles.selected]: selectedThumbButton === 'down',
            })}
          >
            <LikeIcon label={'Not valuable'} spacing="spacious"></LikeIcon>
          </Button>
        </div>
        <Button onClick={showJiraFeedbackCollector} appearance="link">
          Provide detailed feedback
        </Button>
      </div>

      {shouldShowFeedbackDetail && (
        <NegativeFeedbackDetail
          featureName={featureName}
          traceId={traceId}
          feedbackSubmissionCallback={handleFeedbackSubmitted}
          flagTreatment={flagTreatment}
        />
      )}
    </>
  );

  const thankYouMessage = <p>Thank you for your feedback!</p>;

  return (
    <div
      className={cx(styles.feedbackCollectorContainer, {
        [styles.flagTreatment]: flagTreatment,
      })}
    >
      {!wasFeedbackSubmitted && feedbackCollectorContents}
      {wasFeedbackSubmitted && thankYouMessage}
    </div>
  );
};
